/**
 * Cross-side sound playback — Phase 1B-I (2026-05-29 follow-up).
 *
 * When the operator captures a snapshot, the field should hear the
 * shutter chime (so they know the moment is recorded) — and vice
 * versa when the field triggers their own capture. Same goes for the
 * notification chime on incoming chat messages.
 *
 * Wire: the triggering peer plays the sound locally AND broadcasts
 * `system/sound.play { name }` on the LiveKit data channel. Every peer
 * that mounts `useCrossSideSounds(sessionId)` subscribes to that
 * envelope and plays the named sound — except their own emission (the
 * envelope carries `from` we match against to avoid double-play).
 *
 * Keep the envelope name stable; the operator-side bus and the
 * field-side FieldSystemHandler share the same string.
 */
import { useEffect } from "react";

import type { Envelope } from "@/lib/commandBus";
import { useSessionBus } from "@/lib/sessionBus";
import { playSound, type SoundName } from "@/lib/soundEffects";


export const SOUND_PLAY_TOPIC = "system";
export const SOUND_PLAY_TYPE = "sound.play";


/** Subscribe to inbound `system/sound.play` envelopes and play the
 * named sound. The caller mounts this once per session room. Returns
 * void; the unsubscribe is wired into the effect's cleanup. */
export function useCrossSideSounds(): void {
  const bus = useSessionBus((s) => s.bus);
  useEffect(() => {
    if (!bus) return;
    const off = bus.on<{ name: SoundName }>(
      `${SOUND_PLAY_TOPIC}/${SOUND_PLAY_TYPE}`,
      (env: Envelope<{ name: SoundName }>) => {
        const name = env.payload?.name;
        if (!name) return;
        void playSound(name);
      },
    );
    return () => {
      off();
    };
  }, [bus]);
}


/** Play `name` locally + broadcast a `system/sound.play` envelope so
 * the peer hears it too. Fire-and-forget — never throws. Use after a
 * capture mutation resolves, or anywhere else the action should be
 * audible on both sides. */
export async function emitCrossSideSound(name: SoundName): Promise<void> {
  void playSound(name);
  const bus = useSessionBus.getState().bus;
  if (!bus) return;
  try {
    await bus.send({
      topic: SOUND_PLAY_TOPIC,
      type: SOUND_PLAY_TYPE,
      payload: { name },
    });
  } catch {
    // ignore — sound is best-effort overlay
  }
}
